import {
	QrCodeIcon,
	RegisterIcon,
	Store01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback, useState } from "react";
import { BranchSelector } from "@/components/branch-selector";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { LinkDeviceModal } from "@/components/modals/link-device-modal";
import { RenameDeviceModal } from "@/components/modals/rename-device-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DeviceLogsDrawer } from "@/features/devices/_components/device-logs-drawer";
import { DevicesTable } from "@/features/devices/_components/devices-table";
import { CashEventsTable } from "@/features/shifts/_components/cash-events-table";
import { ShiftDetailsModal } from "@/features/shifts/_components/shift-details-modal";
import { ShiftsTable } from "@/features/shifts/_components/shifts-table";
import { useAutoSelectBranch } from "@/hooks/use-auto-select-branch";
import { useMe } from "@/hooks/use-me";
import { usePagination } from "@/hooks/use-pagination";
import { handleConvexError } from "@/lib/errors";
import type { DeviceRenameInput } from "@/lib/schemas/device";
import { toast } from "@/lib/toast";
import type { Device, DeviceLog, LinkTokenResponse } from "@/lib/types/device";
import type { CashEvent, Shift, ShiftDetails } from "@/lib/types/shift";

type TabValue = "shifts" | "logs" | "devices";

interface CashDrawersSearch {
	branch?: string;
	tab: TabValue;
}

export const Route = createFileRoute("/(dashboard)/branch/cash-drawers")({
	component: CashDrawersPage,
	validateSearch: (search: Record<string, unknown>): CashDrawersSearch => ({
		branch: search.branch as string | undefined,
		tab: (search.tab as TabValue) || "shifts",
	}),
});

const PAGE_SIZE = 10;

function CashDrawersPage() {
	const navigate = useNavigate();
	const { branch: searchBranchId, tab } = useSearch({
		from: "/(dashboard)/branch/cash-drawers",
	});

	// Pagination for each tab
	const shiftsPagination = usePagination(PAGE_SIZE);
	const logsPagination = usePagination(PAGE_SIZE);
	const devicesPagination = usePagination(PAGE_SIZE);

	// Modal state
	const [linkModalOpen, setLinkModalOpen] = useState(false);
	const [renameModalOpen, setRenameModalOpen] = useState(false);
	const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
	const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
	const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
	const [deviceToDisconnect, setDeviceToDisconnect] = useState<Device | null>(
		null,
	);

	// Shift details modal
	const [shiftDetailsOpen, setShiftDetailsOpen] = useState(false);
	const [selectedShiftId, setSelectedShiftId] = useState<Id<"shifts"> | null>(
		null,
	);

	// Link token state
	const [linkToken, setLinkToken] = useState<string | undefined>();
	const [linkTokenExpiresAt, setLinkTokenExpiresAt] = useState<
		number | undefined
	>();

	// Loading states
	const [isGeneratingToken, setIsGeneratingToken] = useState(false);
	const [isRenaming, setIsRenaming] = useState(false);
	const [isDisconnecting, setIsDisconnecting] = useState(false);

	// Get current user for org context
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	// Query branches for selector
	const branchesData = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId } : "skip",
	);
	const branches = branchesData ?? [];

	const { branchId, isLoading: isLoadingBranches } = useAutoSelectBranch({
		branchId: searchBranchId,
		branches: branchesData,
		orgId,
	});

	const shiftsData = useQuery(
		api.features.shifts.web.list,
		orgId && branchId ? { branchId: branchId as Id<"branches"> } : "skip",
	);
	const shifts = (shiftsData ?? []) as Shift[];
	const isLoadingShifts =
		isLoadingBranches || (shiftsData === undefined && !!branchId);

	// Query cash events for logs tab
	const cashEventsData = useQuery(
		api.features.shifts.web.getCashEvents,
		orgId && branchId ? { branchId: branchId as Id<"branches"> } : "skip",
	);
	const cashEvents = (cashEventsData ?? []) as CashEvent[];
	const isLoadingLogs =
		isLoadingBranches || (cashEventsData === undefined && !!branchId);

	// Query devices for selected branch
	const devicesData = useQuery(
		api.features.devices.web.listByBranch,
		orgId && branchId ? { branchId: branchId as Id<"branches"> } : "skip",
	);
	const devices = (devicesData ?? []) as Device[];
	const isLoadingDevices =
		isLoadingBranches || (devicesData === undefined && !!branchId);

	// Query shift details when selected
	const shiftDetailsData = useQuery(
		api.features.shifts.web.getDetails,
		orgId && branchId && selectedShiftId
			? {
					branchId: branchId as Id<"branches">,
					shiftId: selectedShiftId,
				}
			: "skip",
	);
	const shiftDetails = shiftDetailsData as ShiftDetails;
	const isLoadingShiftDetails =
		shiftDetailsData === undefined && !!selectedShiftId;

	// Query logs for selected device
	const deviceLogsData = useQuery(
		api.features.deviceLogs.web.getDeviceLogs,
		orgId && branchId && selectedDevice
			? {
					branchId: branchId as Id<"branches">,
					deviceId: selectedDevice._id,
				}
			: "skip",
	);
	const deviceLogs = (deviceLogsData ?? []) as DeviceLog[];

	// Mutations
	const generateLinkTokenMutation = useMutation(
		api.features.devices.web.generateLinkToken,
	);
	const renameDeviceMutation = useMutation(api.features.devices.web.rename);
	const disconnectDeviceMutation = useMutation(
		api.features.devices.web.disconnect,
	);

	// Paginate data (client-side)
	const paginatedShifts = shifts.slice(
		shiftsPagination.pagination.pageIndex *
			shiftsPagination.pagination.pageSize,
		(shiftsPagination.pagination.pageIndex + 1) *
			shiftsPagination.pagination.pageSize,
	);

	const paginatedLogs = cashEvents.slice(
		logsPagination.pagination.pageIndex * logsPagination.pagination.pageSize,
		(logsPagination.pagination.pageIndex + 1) *
			logsPagination.pagination.pageSize,
	);

	// Handle tab change
	const handleTabChange = useCallback(
		(newTab: string) => {
			navigate({
				to: ".",
				search: (prev) => ({
					...prev,
					tab: newTab as TabValue,
				}),
			});
		},
		[navigate],
	);

	// Handle branch selection
	const handleBranchChange = useCallback(
		(value: Id<"branches"> | Id<"branches">[] | undefined) => {
			if (typeof value !== "string") return;
			navigate({
				to: ".",
				search: (prev) => ({ ...prev, branch: value, tab }),
			});
			shiftsPagination.resetPage();
			logsPagination.resetPage();
			devicesPagination.resetPage();
		},
		[
			navigate,
			tab,
			shiftsPagination.resetPage,
			logsPagination.resetPage,
			devicesPagination.resetPage,
		],
	);

	// Shift handlers
	const handleViewShiftDetails = useCallback((shift: Shift) => {
		setSelectedShiftId(shift._id);
		setShiftDetailsOpen(true);
	}, []);

	// Generate link token
	const handleGenerateToken = useCallback(async () => {
		if (!orgId || !branchId) return;

		setIsGeneratingToken(true);
		try {
			const result = (await generateLinkTokenMutation({
				branchId: branchId as Id<"branches">,
			})) as LinkTokenResponse;
			setLinkToken(result.token);
			setLinkTokenExpiresAt(result.expiresAt);
		} catch (error) {
			handleConvexError(error, "Failed to generate link token");
		} finally {
			setIsGeneratingToken(false);
		}
	}, [branchId, generateLinkTokenMutation, orgId]);

	// Device handlers
	const handleRename = useCallback((device: Device) => {
		setSelectedDevice(device);
		setRenameModalOpen(true);
	}, []);

	const handleRenameSubmit = useCallback(
		async (data: DeviceRenameInput) => {
			if (!orgId || !selectedDevice || !branchId) return;

			setIsRenaming(true);
			try {
				await renameDeviceMutation({
					branchId: branchId as Id<"branches">,
					deviceId: selectedDevice._id,
					name: data.name,
				});
				toast.success(<>Device renamed</>);
			} catch (error) {
				handleConvexError(error, "Failed to rename device");
				throw error;
			} finally {
				setIsRenaming(false);
			}
		},
		[branchId, selectedDevice, renameDeviceMutation, orgId],
	);

	const handleViewLogs = useCallback((device: Device) => {
		setSelectedDevice(device);
		setLogsDrawerOpen(true);
	}, []);

	const handleDisconnect = useCallback((device: Device) => {
		setDeviceToDisconnect(device);
		setDisconnectDialogOpen(true);
	}, []);

	const confirmDisconnect = useCallback(async () => {
		if (!orgId || !deviceToDisconnect || !branchId) return;

		setIsDisconnecting(true);
		try {
			await disconnectDeviceMutation({
				branchId: branchId as Id<"branches">,
				deviceId: deviceToDisconnect._id,
			});
			toast.success(<>Device disconnected</>);
			setDisconnectDialogOpen(false);
			setDeviceToDisconnect(null);
		} catch (error) {
			handleConvexError(error, "Failed to disconnect device");
		} finally {
			setIsDisconnecting(false);
		}
	}, [branchId, deviceToDisconnect, disconnectDeviceMutation, orgId]);

	// Clear link token when modal closes
	const handleLinkModalChange = useCallback((open: boolean) => {
		setLinkModalOpen(open);
		if (!open) {
			setLinkToken(undefined);
			setLinkTokenExpiresAt(undefined);
		}
	}, []);
	const [branchSelectorOpen, setBranchSelectorOpen] = useState(false);

	return (
		<PageContent
			title="Cash Drawers"
			controls={
				<div className="flex items-center gap-3">
					<BranchSelector
						branches={branches}
						value={branchId as Id<"branches"> | undefined}
						onValueChange={handleBranchChange}
						placeholder="Select branch"
						className="w-48"
						open={branchSelectorOpen}
						onOpenChange={setBranchSelectorOpen}
						onViewAll={() => navigate({ to: "/settings/branches" })}
						onCreateNew={() => navigate({ to: "/settings/branches" })}
					/>
					{branchId && tab === "devices" && (
						<Can scope="devices.create">
							<ActionHeaderButton
								label="Link Device"
								onClick={() => setLinkModalOpen(true)}
								icon={
									<HugeiconsIcon
										icon={QrCodeIcon}
										size={14}
										strokeWidth={2.5}
									/>
								}
							/>
						</Can>
					)}
				</div>
			}
		>
			<div className="flex flex-1 flex-col gap-4 sm:gap-6">
				{!branchId && !isLoadingBranches ? (
					<div className="flex flex-1 flex-col justify-end pb-12">
						<EmptyStateWithSkeleton
							title="No branches found"
							description="Get started by creating your first branch to manage cash drawers."
							actionLabel="Create branch"
							onAction={() => navigate({ to: "/settings/branches" })}
							icon={
								<HugeiconsIcon
									icon={Store01Icon}
									className="h-5 w-5 text-neutral-700"
									strokeWidth={2.2}
								/>
							}
						/>
					</div>
				) : (
					<div className="flex flex-1 flex-col gap-4">
						{/* Tab Navigation */}
						<Tabs value={tab} onValueChange={handleTabChange}>
							<TabsList>
								<TabsTrigger value="shifts">Shifts</TabsTrigger>
								<TabsTrigger value="logs">Logs</TabsTrigger>
								<TabsTrigger value="devices">Devices</TabsTrigger>
							</TabsList>
						</Tabs>

						{/* Tab Content */}
						{tab === "shifts" &&
							(!isLoadingShifts && shifts.length === 0 ? (
								<div className="flex flex-1 flex-col justify-end pb-12">
									<EmptyStateWithSkeleton
										title="No shifts found"
										description="Opening a shift on a register will start tracking cash flow and transactions"
										icon={
											<HugeiconsIcon
												icon={RegisterIcon}
												className="h-5 w-5 text-neutral-700"
												strokeWidth={2.2}
											/>
										}
									/>
								</div>
							) : (
								<ShiftsTable
									data={paginatedShifts}
									loading={isLoadingShifts}
									pagination={shiftsPagination.pagination}
									onPaginationChange={shiftsPagination.onPaginationChange}
									rowCount={shifts.length}
									onViewDetails={handleViewShiftDetails}
									onRowClick={handleViewShiftDetails}
								/>
							))}

						{tab === "logs" &&
							(!isLoadingLogs && cashEvents.length === 0 ? (
								<div className="flex flex-1 flex-col justify-end pb-12">
									<EmptyStateWithSkeleton
										title="No logs found"
										description="Physical cash drawer events and shift changes will be logged here"
										icon={
											<HugeiconsIcon
												icon={RegisterIcon}
												className="h-5 w-5 text-neutral-700"
												strokeWidth={2.2}
											/>
										}
									/>
								</div>
							) : (
								<CashEventsTable
									data={paginatedLogs}
									loading={isLoadingLogs}
									pagination={logsPagination.pagination}
									onPaginationChange={logsPagination.onPaginationChange}
									rowCount={cashEvents.length}
								/>
							))}

						{tab === "devices" &&
							(!isLoadingDevices && devices.length === 0 ? (
								<div className="flex flex-1 flex-col justify-end pb-12">
									<EmptyStateWithSkeleton
										title="No linked devices"
										description="Link a register or tablet to this branch to start processing cash payments"
										actionLabel="Link Device"
										onAction={() => setLinkModalOpen(true)}
										icon={
											<HugeiconsIcon
												icon={RegisterIcon}
												className="h-5 w-5 text-neutral-700"
												strokeWidth={2.2}
											/>
										}
									/>
								</div>
							) : (
								<DevicesTable
									data={devices}
									loading={isLoadingDevices}
									pagination={devicesPagination.pagination}
									onPaginationChange={devicesPagination.onPaginationChange}
									rowCount={devices.length}
									onRename={handleRename}
									onViewLogs={handleViewLogs}
									onDisconnect={handleDisconnect}
									showPagination={false}
								/>
							))}
					</div>
				)}
			</div>

			{/* Shift Details Modal */}
			<ShiftDetailsModal
				open={shiftDetailsOpen}
				onOpenChange={(open) => {
					setShiftDetailsOpen(open);
					if (!open) setSelectedShiftId(null);
				}}
				shiftDetails={shiftDetails}
				loading={isLoadingShiftDetails}
			/>

			{/* Link Device Modal */}
			<LinkDeviceModal
				open={linkModalOpen}
				onOpenChange={handleLinkModalChange}
				token={linkToken}
				expiresAt={linkTokenExpiresAt}
				onGenerateToken={handleGenerateToken}
				isGenerating={isGeneratingToken}
			/>

			{/* Rename Modal */}
			<RenameDeviceModal
				open={renameModalOpen}
				onOpenChange={setRenameModalOpen}
				device={selectedDevice}
				onSubmit={handleRenameSubmit}
				isSubmitting={isRenaming}
			/>

			{/* Activity Logs Drawer */}
			<DeviceLogsDrawer
				open={logsDrawerOpen}
				onOpenChange={setLogsDrawerOpen}
				device={selectedDevice}
				logs={deviceLogs}
				loading={deviceLogsData === undefined && !!selectedDevice}
			/>

			{/* Disconnect Confirmation */}
			<AlertDialog
				open={disconnectDialogOpen}
				onOpenChange={setDisconnectDialogOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Disconnect device?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to disconnect "{deviceToDisconnect?.name}"?
							The device will need to be re-linked to use it again.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDisconnecting}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDisconnect}
							disabled={isDisconnecting}
						>
							{isDisconnecting ? "Disconnecting..." : "Disconnect"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
